/**
 * AppICEMAP — BottomSheet Custom Element
 * Three-stop swipeable bottom panel for the report list.
 *
 * Tag: <bottom-sheet>
 *
 * States: collapsed (48px handle) | peek (30vh) | expanded (70vh)
 *
 * Attributes:
 *   state — 'collapsed' | 'peek' | 'expanded'
 *
 * Dispatches:
 *   bottom-sheet:state-change → { from, to }
 *   bottom-sheet:drag-start   → { }
 *   bottom-sheet:drag-end     → { finalState }
 *
 * Behavior:
 *   - Touch drag to expand/collapse
 *   - Spring snap to nearest stop on release
 *   - Velocity-based: fast swipe overrides position threshold
 *   - Keyboard: Esc collapses, ArrowUp/Down cycles
 *   - Backdrop blur on map when expanded
 *   - ARIA: role="region", aria-label, aria-expanded
 */

const SNAP_POINTS = {
	collapsed: 48, // px — just the handle
	peek: null, // 30vh — computed dynamically
	expanded: null, // 70vh — computed dynamically
};

const VELOCITY_THRESHOLD = 0.5; // px/ms — minimum velocity for snap override

export class BottomSheet extends HTMLElement {
	/* ── Lifecycle ──────────────────────────────────────────────────────── */

	constructor() {
		super();
		this._state = "peek";
		this._dragY = 0;
		this._startY = 0;
		this._startTranslateY = 0;
		this._isDragging = false;
		this._lastMoveTime = 0;
		this._lastMoveY = 0;
		this._velocity = 0;
		this._rafId = null;
		this._touchStartTime = 0;
	}

	connectedCallback() {
		this._computeSnapPoints();
		this._render();
		this._bindEvents();
		this._applyState(this._state, false);
	}

	disconnectedCallback() {
		this._unbindEvents();
	}

	/* ── Snaps ──────────────────────────────────────────────────────────── */

	_computeSnapPoints() {
		const vh = window.innerHeight;
		SNAP_POINTS.peek = Math.round(vh * 0.3);
		SNAP_POINTS.expanded = Math.round(vh * 0.7);
	}

	_getSnapValue(state) {
		if (state === "collapsed") return SNAP_POINTS.collapsed;
		if (state === "expanded") return SNAP_POINTS.expanded;
		return SNAP_POINTS.peek;
	}

	_getNearestSnap(translateY) {
		const peek = SNAP_POINTS.peek;
		const expanded = SNAP_POINTS.expanded;
		const collapsed = SNAP_POINTS.collapsed;

		// Distance from current translateY to each snap
		const distToCollapsed = Math.abs(translateY - collapsed);
		const distToPeek = Math.abs(translateY - peek);
		const distToExpanded = Math.abs(translateY - expanded);

		if (distToCollapsed <= distToPeek && distToCollapsed <= distToExpanded) {
			return "collapsed";
		}
		if (distToExpanded <= distToPeek && distToExpanded <= distToCollapsed) {
			return "expanded";
		}
		return "peek";
	}

	/* ── Render ─────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `
      <div class="bottom-sheet" data-state="${this._state}">
        <div class="bottom-sheet__handle" aria-hidden="true">
          <div class="bottom-sheet__handle-bar"></div>
        </div>
        <div class="bottom-sheet__content">
          <slot></slot>
        </div>
      </div>
      <div class="bottom-sheet__backdrop" aria-hidden="true"></div>
    `;

		this._sheetEl = this.querySelector(".bottom-sheet");
		this._handleEl = this.querySelector(".bottom-sheet__handle");
		this._contentEl = this.querySelector(".bottom-sheet__content");
		this._backdropEl = this.querySelector(".bottom-sheet__backdrop");
	}

	/* ── Events ─────────────────────────────────────────────────────────── */

	_bindEvents() {
		// Touch events
		if (this._handleEl) {
			this._handleEl.addEventListener(
				"touchstart",
				this._onTouchStart.bind(this),
				{ passive: false },
			);
		}
		if (this._sheetEl) {
			this._sheetEl.addEventListener(
				"touchstart",
				this._onTouchStart.bind(this),
				{ passive: false },
			);
		}
		window.addEventListener("touchmove", this._onTouchMove.bind(this), {
			passive: false,
		});
		window.addEventListener("touchend", this._onTouchEnd.bind(this));

		// Backdrop tap
		if (this._backdropEl) {
			this._backdropEl.addEventListener(
				"click",
				this._onBackdropClick.bind(this),
			);
		}

		// Keyboard
		this.addEventListener("keydown", this._onKeyDown.bind(this));

		// Handle tap (collapsed → peek)
		if (this._handleEl) {
			this._handleEl.addEventListener("click", () => {
				if (this._state === "collapsed") {
					this.setState("peek");
				}
			});
		}

		// Resize
		window.addEventListener("resize", this._onResize.bind(this));
	}

	_unbindEvents() {
		window.removeEventListener("resize", this._onResize);
	}

	_onResize() {
		this._computeSnapPoints();
		this._applyState(this._state, false);
	}

	_onTouchStart(e) {
		this._isDragging = true;
		this._startY = e.touches[0].clientY;
		this._touchStartTime = Date.now();

		const currentTranslateY = this._getCurrentTranslateY();
		this._startTranslateY = currentTranslateY;
		this._dragY = currentTranslateY;
		this._lastMoveY = currentTranslateY;
		this._lastMoveTime = this._touchStartTime;

		if (this._sheetEl) {
			this._sheetEl.style.transition = "none";
		}

		this.dispatchEvent(
			new CustomEvent("bottom-sheet:drag-start", {
				bubbles: true,
				detail: {},
			}),
		);
	}

	_onTouchMove(e) {
		if (!this._isDragging) return;

		const currentY = e.touches[0].clientY;
		const delta = this._startY - currentY;
		const newTranslateY = this._startTranslateY - delta;

		// Clamp between expanded and collapsed
		const maxTranslateY = SNAP_POINTS.collapsed;
		const minTranslateY = SNAP_POINTS.expanded;
		const clamped = Math.max(
			minTranslateY,
			Math.min(maxTranslateY, newTranslateY),
		);

		this._dragY = clamped;
		this._lastMoveY = clamped;
		this._lastMoveTime = Date.now();

		// Calculate velocity for snap decision
		const dt = this._lastMoveTime - this._touchStartTime;
		if (dt > 0) {
			this._velocity = (this._dragY - this._startTranslateY) / dt;
		}

		// Apply immediately for responsiveness
		if (this._sheetEl) {
			const sheetHeight = this._sheetEl.offsetHeight;
			const translatePx = sheetHeight - clamped;
			this._sheetEl.style.transform = `translateY(${translatePx}px)`;
		}

		// Prevent default to stop map scrolling
		e.preventDefault();
	}

	_onTouchEnd() {
		if (!this._isDragging) return;
		this._isDragging = false;

		// Determine target state
		let targetState;

		// Velocity-based snap override
		const absVelocity = Math.abs(this._velocity);
		if (absVelocity > VELOCITY_THRESHOLD) {
			// Fast swipe up → expand, fast swipe down → collapse/peek
			if (this._velocity < -VELOCITY_THRESHOLD) {
				targetState = "expanded";
			} else if (this._velocity > VELOCITY_THRESHOLD) {
				// Check if we're already near peek
				const currentPos = this._dragY;
				const distToCollapsed = Math.abs(currentPos - SNAP_POINTS.collapsed);
				const distToPeek = Math.abs(currentPos - SNAP_POINTS.peek);
				targetState = distToCollapsed < distToPeek ? "collapsed" : "peek";
			} else {
				targetState = this._getNearestSnap(this._dragY);
			}
		} else {
			targetState = this._getNearestSnap(this._dragY);
		}

		this.setState(targetState);

		this.dispatchEvent(
			new CustomEvent("bottom-sheet:drag-end", {
				bubbles: true,
				detail: { finalState: targetState },
			}),
		);
	}

	_onBackdropClick() {
		if (this._state === "expanded") {
			this.setState("peek");
		}
	}

	_onKeyDown(e) {
		if (e.key === "Escape") {
			if (this._state === "expanded") {
				this.setState("peek");
			} else if (this._state === "peek") {
				this.setState("collapsed");
			}
		} else if (e.key === "ArrowUp") {
			if (this._state === "collapsed") this.setState("peek");
			else if (this._state === "peek") this.setState("expanded");
		} else if (e.key === "ArrowDown") {
			if (this._state === "expanded") this.setState("peek");
			else if (this._state === "peek") this.setState("collapsed");
		}
	}

	/* ── State ──────────────────────────────────────────────────────────── */

	get state() {
		return this._state;
	}

	/**
	 * Programmatically set the sheet state.
	 * @param {'collapsed' | 'peek' | 'expanded'} newState
	 * @param {{ animate?: boolean }} [options]
	 */
	setState(newState, options = {}) {
		const { animate = true } = options;
		const oldState = this._state;

		if (oldState === newState) return;

		this._state = newState;
		this._applyState(newState, animate);

		this.dispatchEvent(
			new CustomEvent("bottom-sheet:state-change", {
				bubbles: true,
				detail: { from: oldState, to: newState },
			}),
		);
	}

	_applyState(state, animate) {
		if (!this._sheetEl) return;

		const snapValue = this._getSnapValue(state);
		const sheetHeight = this._sheetEl.offsetHeight;
		const translatePx = sheetHeight - snapValue;

		if (animate) {
			this._sheetEl.style.transition = `transform var(--duration-normal, 250ms) var(--easing-spring, cubic-bezier(0.34, 1.56, 0.64, 1))`;
		} else {
			this._sheetEl.style.transition = "none";
		}

		this._sheetEl.style.transform = `translateY(${translatePx}px)`;
		this._sheetEl.dataset.state = state;
		this.setAttribute("data-state", state);

		// Backdrop visibility
		if (this._backdropEl) {
			if (state === "expanded") {
				this._backdropEl.classList.add("bottom-sheet__backdrop--visible");
			} else {
				this._backdropEl.classList.remove("bottom-sheet__backdrop--visible");
			}
		}

		// ARIA
		this.setAttribute("aria-expanded", state === "expanded" ? "true" : "false");
	}

	_getCurrentTranslateY() {
		if (this._sheetEl && this._sheetEl.style.transform) {
			const match = this._sheetEl.style.transform.match(
				/translateY\(([-\d.]+)px\)/,
			);
			if (match) {
				const sheetHeight = this._sheetEl.offsetHeight;
				return sheetHeight - parseFloat(match[1]);
			}
		}
		return SNAP_POINTS.peek;
	}

	/* ── Attributes ────────────────────────────────────────────────────── */

	static get observedAttributes() {
		return ["data-state"];
	}

	attributeChangedCallback(name, oldVal, newVal) {
		if (name === "data-state" && oldVal !== newVal) {
			if (["collapsed", "peek", "expanded"].includes(newVal)) {
				this.setState(newVal);
			}
		}
	}
}

customElements.define("bottom-sheet", BottomSheet);
